import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  DEFAULT_SETTINGS,
  type RumView,
  type SessionView,
} from '@cercle/shared';
import { useApi } from '@/shared/app-context';
import { useAsync } from '@/shared/hooks/useAsync';
import { useSessionSocket } from '@/shared/ws/useSessionSocket';
import { errorMessage } from '@/shared/http/http-client';
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  ErrorText,
  Kicker,
  Muted,
  Field,
  Input,
  Skeleton,
  Switch,
} from '@/shared/ui';
import { formatSessionDate, formatSessionTimeRange } from '../session/format';
import { endFromStart, toIsoFromLocalInput, toLocalInput } from './session-form';
import styles from './admin.module.scss';

/**
 * Écran 10 — conduite de séance (US-11, US-13, US-15, US-16).
 *
 * Sans identifiant dans l'URL, l'écran pilote la prochaine séance : en pratique le
 * fondateur ouvre la régie depuis son téléphone au début de la soirée, il ne va pas
 * chercher un identifiant.
 */
export function AdminSessionScreen(): ReactNode {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const session = useAsync(
    () => (id ? api.sessions.byId(id) : api.sessions.next()),
    [id],
  );

  /**
   * La régie écoute la séance, comme tous les autres écrans.
   *
   * Elle était la seule à ne rafraîchir qu'après ses propres actions. Or c'est l'écran où
   * le décalage se voit le plus : le maître de chai regarde monter les notes pendant que
   * le cercle goûte, et un compteur figé lui ferait croire que personne ne valide.
   */
  useSessionSocket(session.data?.id ?? null, {
    onTastingOpened: session.reload,
    onRumRevealed: session.reload,
    onSessionClosed: session.reload,
    onProgressChanged: session.reload,
  });

  if (session.loading) return <Skeleton />;
  if (session.error) return <EmptyState>{session.error}</EmptyState>;
  if (!session.data) return <EmptyState>Aucune séance à conduire.</EmptyState>;

  const data: SessionView = session.data;
  const closed = data.status === 'CLOSED';

  async function act(action: () => Promise<unknown>): Promise<void> {
    setError(null);
    try {
      await action();
      session.reload();
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  /** L'ordre visé après avoir déplacé le rhum `from` de `delta` rangs. */
  function moved(from: number, delta: number): string[] {
    const ids = data.rums.map((slot) => slot.rum.id);
    const pulled = ids[from];
    // `noUncheckedIndexedAccess` a raison d'insister : l'appelant borne déjà l'index par
    // les boutons désactivés, mais rien dans la signature ne le dit.
    if (pulled === undefined) return ids;
    ids.splice(from, 1);
    ids.splice(from + delta, 0, pulled);
    return ids;
  }

  async function remove(): Promise<void> {
    if (!window.confirm('Supprimer cette séance ? Elle n’a encore recueilli aucune note.')) {
      return;
    }
    setError(null);
    try {
      await api.admin.deleteSession(data.id);
      void navigate('/regie/seances');
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  return (
    <div className={styles.page}>
      <header>
        <Kicker>
          <Link to="/regie/seances">Régie</Link> · séance n°{data.number}
        </Kicker>
        <h1>{formatSessionDate(data.startsAt)}</h1>
        <Muted>
          {formatSessionTimeRange(data.startsAt, data.endsAt)}
          {data.location ? ` · ${data.location}` : ''} ·{' '}
          {data.attendances.filter((a) => a.confirmed).length} présents,{' '}
          {data.attendances.reduce((sum, a) => sum + a.guestCount, 0)} invité(s)
        </Muted>
      </header>

      <ErrorText>{error}</ErrorText>

      <section>
        <h2 className={styles.sectionTitle}>
          Rhums de la séance <Muted>· {data.rums.length} sur 3</Muted>
        </h2>
        <ul className={styles.rows}>
          {data.rums.map((slot, index) => (
            <li key={slot.rum.id} className={styles.row}>
              <span className={styles.rank}>{slot.orderIndex}</span>
              <span className={styles.rowInfo}>
                <strong>{slot.rum.name}</strong>
                <Muted>
                  {slot.revealedAt
                    ? 'Révélé'
                    : slot.isBlind
                      ? 'Masqué pour tous'
                      : [slot.rum.country, slot.rum.broughtBy?.pseudo].filter(Boolean).join(' · ')}
                </Muted>
              </span>

              {/*
                L'interrupteur doré : le mode aveugle n'est pas une préférence, c'est un
                état de valeur — d'où l'or plutôt que le cuivre.
              */}
              <span className={styles.blindControl}>
                <span className={styles.blindLabel}>Aveugle</span>
                <Switch
                  gold
                  checked={slot.isBlind}
                  disabled={closed || slot.revealedAt !== null}
                  label={`Mode aveugle pour ${slot.rum.name}`}
                  onChange={(next) => void act(() => api.admin.setBlind(data.id, slot.rum.id, next))}
                />
              </span>

              {slot.isBlind && !slot.revealedAt && !closed ? (
                <Button onClick={() => void act(() => api.admin.reveal(data.id, slot.rum.id))}>
                  Révéler
                </Button>
              ) : null}

              {/*
                Monter / descendre plutôt qu'un glisser-déposer : la régie se tient debout,
                une bouteille à la main, et un glissé se rate. Cela reste par ailleurs le
                seul réordonnancement atteignable au clavier.
              */}
              {!closed ? (
                <span className={styles.orderControls}>
                  <Button
                    variant="ghost"
                    disabled={index === 0}
                    aria-label={`Monter ${slot.rum.name}`}
                    onClick={() => void act(() => api.admin.reorderRums(data.id, moved(index, -1)))}
                  >
                    ↑
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={index === data.rums.length - 1}
                    aria-label={`Descendre ${slot.rum.name}`}
                    onClick={() => void act(() => api.admin.reorderRums(data.id, moved(index, 1)))}
                  >
                    ↓
                  </Button>
                  <Button
                    variant="ghost"
                    aria-label={`Retirer ${slot.rum.name} du programme`}
                    onClick={() => void act(() => api.admin.detachRum(data.id, slot.rum.id))}
                  >
                    Retirer
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
        <Muted>L’interrupteur doré masque la fiche pour tous, rhum par rhum.</Muted>

        {!closed ? (
          <AttachRum session={data} onDone={session.reload} onError={setError} />
        ) : null}
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Avancement</h2>
        <Card className={styles.progress}>
          <div>
            <span>Notes déposées</span>
            <strong>
              {data.progress.validatedTastings} / {data.progress.expectedTasters * data.rums.length}
            </strong>
          </div>
          <div>
            <span>Pronostics</span>
            <strong>
              {data.progress.lockedGuesses} /{' '}
              {data.progress.expectedTasters * data.rums.filter((r) => r.isBlind).length}
            </strong>
          </div>
        </Card>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Présents</h2>
        <ul className={styles.avatars}>
          {data.attendances
            .filter((a) => a.confirmed)
            .map((a) => (
              <li key={a.member.id} title={a.member.pseudo}>
                <Avatar pseudo={a.member.pseudo} url={a.member.avatarUrl} />
              </li>
            ))}
        </ul>
      </section>

      {!closed ? <EditSession session={data} onDone={session.reload} onError={setError} /> : null}

      <footer className={styles.adminActions}>
        {data.status === 'PLANNED' ? (
          <Button onClick={() => void act(() => api.admin.openSession(data.id))}>
            Ouvrir les notes
          </Button>
        ) : null}
        {!closed ? (
          <Button
            variant="secondary"
            onClick={() => void act(() => api.admin.closeSession(data.id))}
          >
            Clôturer la séance
          </Button>
        ) : (
          <Muted>Séance clôturée : les notes sont figées.</Muted>
        )}

        {/*
          La suppression n'apparaît que tant que la séance n'a rien recueilli. L'API tient
          la règle — une note validée fait entrer la séance dans l'histoire du cercle — et
          l'écran s'aligne dessus pour ne pas proposer un bouton qui refuserait.
        */}
        {data.progress.validatedTastings === 0 && !closed ? (
          <Button variant="ghost" onClick={() => void remove()}>
            Supprimer cette séance
          </Button>
        ) : null}
      </footer>
    </div>
  );
}

interface PanelProps {
  session: SessionView;
  onDone: () => void;
  onError: (message: string | null) => void;
}

/**
 * « + Rattacher un rhum » (US-11).
 *
 * La liste vient de la Cave, filtrée sur ce qui reste à déguster et débarrassé de ce qui
 * est déjà au programme : proposer une bouteille déjà inscrite ne mènerait qu'à un refus
 * de l'API.
 */
function AttachRum({ session, onDone, onError }: PanelProps): ReactNode {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const cave = useAsync(() => api.rums.search({ status: 'TO_TASTE' }), []);

  if (!open) {
    return (
      <Button variant="dashed" block onClick={() => setOpen(true)}>
        + Rattacher un rhum
      </Button>
    );
  }

  const already = new Set(session.rums.map((slot) => slot.rum.id));
  const candidates = (cave.data?.items ?? []).filter((rum: RumView) => !already.has(rum.id));

  async function attach(rumId: string): Promise<void> {
    setBusy(true);
    onError(null);
    try {
      await api.admin.attachRum(session.id, {
        rumId,
        orderIndex: session.rums.length + 1,
        isBlind: false,
      });
      setOpen(false);
      onDone();
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className={styles.picker}>
      <h3 className={styles.sectionTitle}>Choisir une bouteille</h3>
      {cave.loading ? <Skeleton lines={3} /> : null}
      {!cave.loading && candidates.length === 0 ? (
        <Muted>Aucune bouteille disponible : toute la cave est déjà au programme.</Muted>
      ) : null}
      <ul className={styles.rows}>
        {candidates.map((rum: RumView) => (
          <li key={rum.id} className={styles.row}>
            <span className={styles.rowInfo}>
              <strong>{rum.name}</strong>
              <Muted>{[rum.country, rum.distillery].filter(Boolean).join(' · ')}</Muted>
            </span>
            <Button disabled={busy} onClick={() => void attach(rum.id)}>
              Rattacher
            </Button>
          </li>
        ))}
      </ul>
      <Link className={styles.newRumLink} to={`/rhums/nouveau?seance=${session.id}&regie=1`}>
        La bouteille n’est pas dans la cave ? Créer sa fiche
      </Link>

      <Button variant="ghost" onClick={() => setOpen(false)}>
        Annuler
      </Button>
    </Card>
  );
}

/**
 * Date, créneau et lieu.
 *
 * Les champs sont pré-remplis avec l'existant : une régie corrige plus souvent un lieu
 * qu'elle ne saisit une séance de zéro. L'heure passe en local pour la saisie et repart
 * en ISO — `datetime-local` ne connaît pas les fuseaux. Une séance sans fin — le cas des
 * séances posées à la main avant que le champ existe — s'en voit proposer une, calculée sur
 * la durée habituelle du cercle.
 */
function EditSession({ session, onDone, onError }: PanelProps): ReactNode {
  const api = useApi();
  const settings = useAsync(() => api.settings.read(), []);
  const durationMinutes =
    settings.data?.sessionDurationMinutes ?? DEFAULT_SETTINGS.sessionDurationMinutes;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [startsAt, setStartsAt] = useState(() => toLocalInput(session.startsAt));
  const [endsAt, setEndsAt] = useState(() => (session.endsAt ? toLocalInput(session.endsAt) : ''));
  const [location, setLocation] = useState(session.location ?? '');
  // Déplacer le début redéplace la fin, jusqu'à ce que la régie fixe elle-même celle-ci.
  const [endEditedByHand, setEndEditedByHand] = useState(false);

  function changeStart(value: string): void {
    setStartsAt(value);
    if (!endEditedByHand) setEndsAt(endFromStart(value, durationMinutes));
  }

  if (!open) {
    // La proposition de fin se calcule à l'ouverture, pas au montage : les réglages du cercle
    // arrivent après le premier rendu.
    return (
      <Button
        variant="ghost"
        onClick={() => {
          if (!endsAt) setEndsAt(endFromStart(startsAt, durationMinutes));
          setOpen(true);
        }}
      >
        Modifier la date ou le lieu
      </Button>
    );
  }

  async function save(): Promise<void> {
    if (endsAt && new Date(endsAt).getTime() <= new Date(startsAt).getTime()) {
      onError('La fin doit venir après le début.');
      return;
    }
    setBusy(true);
    onError(null);
    try {
      await api.admin.updateSession(session.id, {
        startsAt: toIsoFromLocalInput(startsAt),
        endsAt: endsAt ? toIsoFromLocalInput(endsAt) : null,
        location: location.trim() || null,
      });
      setOpen(false);
      onDone();
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className={styles.picker}>
      <h3 className={styles.sectionTitle}>Modifier la séance</h3>
      <Field label="Début">
        <Input
          type="datetime-local"
          value={startsAt}
          onChange={(event) => changeStart(event.target.value)}
        />
      </Field>
      <Field label="Fin" hint="Vide : la fin sera fixée à la clôture.">
        <Input
          type="datetime-local"
          value={endsAt}
          min={startsAt || undefined}
          onChange={(event) => {
            setEndEditedByHand(true);
            setEndsAt(event.target.value);
          }}
        />
      </Field>
      <Field label="Lieu">
        <Input
          value={location}
          onChange={(event) => setLocation(event.target.value)}
          placeholder="Salle Courbet"
        />
      </Field>
      <div className={styles.adminActions}>
        <Button disabled={busy} onClick={() => void save()}>
          {busy ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Annuler
        </Button>
      </div>
    </Card>
  );
}
